import { useMemo } from "react";

import { emailTemplatesApi } from "@/api";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import withAuthorization from "@/common/hocs/with-authorization/withAuthorization";
import { TemplateManagement } from "@/pages/system-settings/tabs/templates/template-management/TemplateManagement";
import {
  TemplateManagementConfig,
  TemplateSubjectMaxLength,
  TemplateTitleMaxLength,
} from "@/pages/system-settings/tabs/templates/template-management/TemplateManagement.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

function EmailTemplatesSettingsInner() {
  const { t } = useTranslation();

  const config = useMemo<TemplateManagementConfig>(
    () => ({
      queryKey: QueryKey.EmailTemplates,
      titleMaxLength: TemplateTitleMaxLength,
      subject: {
        label: t("settings.emailTemplates.subject"),
        maxLength: TemplateSubjectMaxLength,
      },
      api: {
        getAll: () => emailTemplatesApi.getAllEmailTemplates(),
        create: (values) =>
          emailTemplatesApi.createEmailTemplate({
            createEmailTemplateRequest: {
              title: values.title,
              subject: values.subject ?? "",
              content: values.content,
            },
          }),
        update: (id, values) =>
          emailTemplatesApi.updateEmailTemplate({
            id,
            updateEmailTemplateRequest: {
              title: values.title,
              subject: values.subject ?? "",
              content: values.content,
            },
          }),
        remove: (id) => emailTemplatesApi.deleteEmailTemplate({ id }),
      },
      labels: {
        selectTemplate: t("settings.emailTemplates.selectTemplate"),
        addTemplate: t("settings.emailTemplates.addTemplate"),
        title: t("settings.emailTemplates.title"),
        content: t("settings.emailTemplates.content"),
        noTemplateSelected: t("settings.emailTemplates.noTemplateSelected"),
        noTemplates: t("settings.emailTemplates.noTemplates"),
        saveSuccess: t("settings.emailTemplates.saveSuccess"),
        saveError: t("settings.emailTemplates.saveError"),
        createSuccess: t("settings.emailTemplates.createSuccess"),
        createError: t("settings.emailTemplates.createError"),
        deleteSuccess: t("settings.emailTemplates.deleteSuccess"),
        deleteError: t("settings.emailTemplates.deleteError"),
        deleteConfirmTitle: t("settings.emailTemplates.deleteConfirmTitle"),
        deleteConfirmText: t("settings.emailTemplates.deleteConfirmText"),
      },
    }),
    [t]
  );

  return <TemplateManagement config={config} />;
}

export const EmailTemplatesSettings = withAuthorization(EmailTemplatesSettingsInner, UserPermission.Supervisor);
